import ImportModel, { IMPORT_TYPES } from 'cio/models/import';
import { IdentifierKey, ObjectIdentifierKey } from 'cio/utils/identifiers';

export interface ImportPreprocessMeta {
  warnings: string[];
  rows: number;
  existingProfiles: number;
  newProfiles: number;
  attributes: string[];
  profilesToUpdate: number;
  emptyCell: boolean;
  samples: string[][];
  timestampFormat: string;
  invalidRows: number;
  invalidExport: number;
  warningRows: number;
  warningExport: number;
  columnWarnings: Record<string, string[]>;
}

export const IMPORT_PREPROCESS_META_DEFAULTS = {
  warnings: () => [],
  attributes: () => [],
  samples: () => [],
  columnWarnings: () => ({}),
};

export interface ImportValidateRules {
  noHeader: boolean;
  useColumnIndex: number;
  onlyExisting: boolean;
  excludeExisting: boolean;
  matchEmail: boolean;
  noId: boolean;
  identifier?: IdentifierKey | ObjectIdentifierKey.ObjectId;
  skipAttributes: boolean[];
  mapAttributes: string[];
  ignoreRestrictedAttributes: string[];
}

export const IMPORT_VALIDATE_RULES_DEFAULTS = {
  noHeader: () => false,
  useColumnIndex: () => null,
  onlyExisting: () => false,
  excludeExisting: () => false,
  matchEmail: () => false,
  noId: () => false,
  identifier: () => IdentifierKey.Id,
  skipAttributes: () => [],
  mapAttributes: () => [],
  ignoreRestrictedAttributes: () => [],
};

export interface ImportValidateMeta {
  warnings?: string[];
  errors?: string[];
  rows: number;
  existingProfiles?: number;
  newProfiles?: number;
  existingObjects?: number;
  newObjects?: number;
  existingRelationships?: number;
  newRelationships?: number;
  emailChanges: number;
  idChanges: number;
  profilesToUpdate: number;
  emptyCell: boolean;
  invalidRows: number;
  invalidExport: number;
  columnWarnings: Record<string, any>;
  warningRows: number;
  warningExport: number;
  eventCardinality?: number; // only on event imports
  timestampFormat?: string; // only on event imports
}

export interface ImportRules {
  clearEmptyCells: boolean;
}

export const IMPORT_RULES_DEFAULTS = {
  clearEmptyCells: () => false,
};

export interface ImportMeta {
  rows: number;
  duration: number;
}

export function getImportTargetData(record: ImportModel) {
  let target = 'person';
  let newTargetCount = record.newProfileCount;
  let newProfiles = record.validateMeta?.newProfiles;
  let existingTargetCount = record.existingProfileCount;
  let existingProfiles = record.validateMeta?.existingProfiles;

  if (record.type === IMPORT_TYPES.object) {
    target = (record.objectType?.singularName ?? 'object').toLowerCase();
    newTargetCount = record.newObjectsCount;
    newProfiles = record.validateMeta?.newObjects;
    existingTargetCount = record.existingObjectsCount;
    existingProfiles = record.validateMeta?.existingObjects;
  }

  if (record.type === IMPORT_TYPES.relationship) {
    target = 'relationship';
    newTargetCount = record.newRelationshipsCount;
    newProfiles = record.validateMeta?.newRelationships;
    existingTargetCount = record.existingRelationshipsCount;
    existingProfiles = record.validateMeta?.existingRelationships;
  }

  return {
    target,
    newTargetCount,
    newProfiles,
    existingTargetCount,
    existingProfiles,
  };
}
